/**
 * /app/bundles — superficie comercial de Bundles (Flow Engine = infra interna).
 *
 * Modelo de producto (claro):
 *   Paso 1 → elegís X (caja)   · por categoría o productos concretos
 *   Paso 2 → elegís Y (bebida)
 *   Paso 3 → elegís Z (snack)
 *   …suma precios del catálogo…
 *   Regla opcional → dto/recargo según una elección previa (ej. caja Premium)
 *   Publicar = URL viva · Home = bloque Bundle en Experiencia
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { DataTable, Loading, LifecycleBadge, CrudHelpCard, FieldHelp } from "@/components/ds";
import { CrudShellHeader, CrudEmptyState } from "@/components/ds/CrudShell";
import type { DataTableColumn } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ImageUploadField } from "@/components/ImageUploadField";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  Gift,
  Loader2,
  Monitor,
  Pencil,
  Plus,
  Search,
  Smartphone,
  Trash2,
  Upload,
} from "lucide-react";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { RenataLauncher } from "@/components/renata/RenataLauncher";
import { setRenataWorkspaceFocus } from "@/lib/renata/workspace-focus";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { CrudEditorShell } from "@/components/workspace/CrudEditorShell";
import { FieldLabel } from "@/components/help/FieldHelpTip";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import type {
  FlowDefinition,
  FlowRule,
  FlowSourceBinding,
  FlowStep,
  FlowStepType,
} from "@/lib/flow-engine/types";
import { validateFlowDefinition } from "@/lib/flow-engine/validate";
import { BundleRuntime } from "@/components/bundles/BundleRuntime";
import type { MarketProduct } from "@/lib/market-catalog";
import { BUNDLE_TEMPLATES, type BundleTemplateId } from "@/lib/bundles/preset";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { lifecycleStatusLabel } from "@/lib/product-surface";

export const Route = createFileRoute("/app/bundles")({
  component: BundlesPage,
  head: () => ({ meta: [{ title: "Packs — Structura Market" }] }),
});

type BundleListItem = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  lifecycleStatus: string;
  version: number;
  dirty: boolean;
  updatedAt: string;
  etag: string;
  productStepCount?: number;
};

type BundleRecord = BundleListItem & {
  draft: FlowDefinition;
  published: FlowDefinition | null;
};

type CategoryHit = { id: string; name: string };

type SourceMode = "all" | "category" | "products";

const STEP_TYPE_LABEL: Partial<Record<FlowStepType, string>> = {
  product_picker: "Elegir productos",
  radio: "Opción (sin precio)",
  textarea: "Mensaje de texto",
  text_input: "Texto corto",
  summary: "Resumen y total",
  container: "Pack con compartimentos",
  message: "Aviso",
};

function unwrapProductHits(raw: unknown): Array<{
  id: string;
  name: string;
  imagenUrl: string | null;
  precioUnitario: number | null;
  slug: string;
  categoriaId?: string | null;
  tags?: string[];
  activo?: boolean;
}> {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray((raw as { items?: unknown }).items)) {
    return (raw as { items: ReturnType<typeof unwrapProductHits> }).items;
  }
  return [];
}

function unwrapCategories(raw: unknown): CategoryHit[] {
  if (Array.isArray(raw)) {
    return raw
      .map((c) => {
        const row = c as { id?: string; name?: string };
        return row.id && row.name ? { id: String(row.id), name: String(row.name) } : null;
      })
      .filter((c): c is CategoryHit => Boolean(c));
  }
  return [];
}

function newStep(partial: Partial<FlowStep> & Pick<FlowStep, "type" | "label">): FlowStep {
  const id = `step_${crypto.randomUUID().replace(/-/g, "").slice(0, 10)}`;
  return {
    id,
    key: partial.key ?? id,
    type: partial.type,
    label: partial.label,
    description: partial.description,
    order: partial.order ?? 0,
    enabled: partial.enabled ?? true,
    required: partial.required ?? false,
    selectionMode: partial.selectionMode ?? (partial.type === "product_picker" ? "single" : "none"),
    qty: partial.qty ?? { mode: "none" },
    parentId: partial.parentId ?? null,
    container: partial.container,
    sourceBindingIds: partial.sourceBindingIds ?? [],
    field: partial.field,
    helpText: partial.helpText,
    layout: partial.layout,
  };
}

function bindingForStep(draft: FlowDefinition, step: FlowStep): FlowSourceBinding | undefined {
  const id = step.sourceBindingIds[0];
  return id ? draft.sourceBindings.find((b) => b.id === id) : undefined;
}

function sourceModeOf(binding: FlowSourceBinding | undefined): SourceMode {
  const q = binding?.query ?? {};
  if (Array.isArray(q.productIds)) return "products";
  if (typeof q.categoriaId === "string" && q.categoriaId) return "category";
  return "all";
}

function BundlesPage() {
  const tid = useWorkspaceTenantId();
  const [items, setItems] = useState<BundleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [editing, setEditing] = useState<BundleRecord | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createTemplate, setCreateTemplate] = useState<BundleTemplateId>("default");
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState<MarketProduct[]>([]);
  const [categories, setCategories] = useState<CategoryHit[]>([]);

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    setLock(null);
    try {
      const data = await apiGetJson<BundleListItem[]>("/api/bundles", tid);
      setItems(data);
    } catch (e) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) setLock(err.planLock);
      else toast.error("No se pudieron cargar los packs");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  const openEditor = async (id: string) => {
    try {
      const rec = await apiGetJson<BundleRecord>(`/api/bundles/${id}`, tid);
      setEditing(rec);
      const [rawProducts, rawCats] = await Promise.all([
        apiGetJson<unknown>("/api/products", tid),
        apiGetJson<unknown>("/api/categories", tid),
      ]);
      const hits = unwrapProductHits(rawProducts);
      setProducts(
        hits.map((h) => ({
          id: h.id,
          name: h.name,
          slug: h.slug,
          imagenUrl: h.imagenUrl,
          precioUnitario: h.precioUnitario,
          precioPorKg: null,
          categoriaId: h.categoriaId ?? null,
          categoriaName: null,
          description: null,
          tipoVenta: "unidad" as const,
          unidadMedida: "u",
          tags: h.tags ?? [],
          activo: h.activo ?? true,
          sortOrder: 0,
        })),
      );
      setCategories(unwrapCategories(rawCats));
    } catch {
      toast.error("No se pudo abrir el pack");
    }
  };

  const createBundle = async () => {
    if (!createName.trim()) return;
    setSaving(true);
    try {
      const rec = await apiSendJson<BundleRecord>("/api/bundles", "POST", { name: createName.trim(), templateId: createTemplate }, tid);
      setCreateOpen(false);
      setCreateName("");
      toast.success("Pack creado");
      await load();
      await openEditor(rec.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo crear");
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async (draft: FlowDefinition, etag: string) => {
    if (!editing) return;
    setSaving(true);
    try {
      const rec = await apiSendJson<BundleRecord>(`/api/bundles/${editing.id}`, "PUT", { draft, etag }, tid);
      setEditing(rec);
      toast.success("Borrador guardado");
      await load();
    } catch (e) {
      const err = e as { status?: number };
      toast.error(err.status === 409 ? "Conflicto de versión — recargá" : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!editing) return;
    const report = validateFlowDefinition(editing.draft);
    if (!report.ok) {
      toast.error(report.errors[0]?.message ?? "Validación fallida");
      return;
    }
    setSaving(true);
    try {
      const rec = await apiSendJson<BundleRecord>(
        `/api/bundles/${editing.id}?action=publish`,
        "POST",
        { etag: editing.etag },
        tid,
      );
      setEditing(rec);
      toast.success("Pack publicado");
      await load();
    } catch {
      toast.error("No se pudo publicar");
    } finally {
      setSaving(false);
    }
  };

  const columns: DataTableColumn<BundleListItem>[] = [
    {
      key: "name",
      header: "Pack",
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium truncate">{r.name}</div>
          {r.description ? (
            <div className="text-xs text-muted-foreground truncate max-w-[280px]">{r.description}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: "status",
      header: "Estado",
      render: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <LifecycleBadge status={r.lifecycleStatus} />
          {r.dirty && r.lifecycleStatus === "published" ? (
            <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
              {lifecycleStatusLabel("dirty")}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "products",
      header: "Productos",
      className: "text-sm text-muted-foreground",
      render: (r) => {
        const n = r.productStepCount ?? 0;
        if (!n) return "Sin pasos de producto";
        return `${n} paso${n === 1 ? "" : "s"} de producto`;
      },
    },
    {
      key: "price",
      header: "Precio",
      className: "text-sm text-muted-foreground",
      render: () => "Según elección",
    },
    {
      key: "updated",
      header: "Última actualización",
      className: "hidden md:table-cell text-xs text-muted-foreground",
      render: (r) => new Date(r.updatedAt).toLocaleString("es-AR"),
    },
    {
      key: "actions",
      header: "Acciones",
      render: (r) => (
        <div className="flex gap-1 justify-end">
          <Button size="sm" variant="ghost" title="Editar" onClick={() => void openEditor(r.id)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title="Duplicar"
            onClick={async () => {
              await apiSendJson(`/api/bundles/${r.id}?action=duplicate`, "POST", {}, tid);
              toast.success("Duplicado");
              await load();
            }}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title="Archivar"
            onClick={async () => {
              await apiDelete(`/api/bundles/${r.id}`, tid);
              toast.success("Archivado");
              await load();
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];


  if (lock) return <FeatureLockedState payload={lock} />;

  if (editing) {
    return (
      <BundleEditor
        record={editing}
        products={products}
        categories={categories}
        saving={saving}
        onBack={() => {
          setEditing(null);
          void load();
        }}
        onChangeDraft={(draft) => setEditing({ ...editing, draft })}
        onSave={() => void saveDraft(editing.draft, editing.etag)}
        onPublish={() => void publish()}
      />
    );
  }

  return (
    <div>
      <CrudShellHeader
        title="Packs"
        description="El cliente arma el pack paso a paso. Cada paso es una elección; el total suma precios del catálogo."
        backHref="/app/catalogacion"
        backLabel="Volver a Catalogación"
        count={items.length}
        action={<Button onClick={() => setCreateOpen(true)}><Plus className="mr-2 h-4 w-4" />Crear pack</Button>}
      />
      <ClinicalPageShell offset="none" className="pt-6">
        {loading ? (
          <Loading />
        ) : (
          <DataTable
            columns={columns}
            rows={items}
            getRowKey={(r) => r.id}
            emptyState={
              <CrudEmptyState
                icon={<Gift className="h-8 w-8" />}
                title="Sin packs"
                description="Creá un pack con pasos de elección. El cliente arma el suyo y ve el total."
                action={<Button onClick={() => setCreateOpen(true)}>Crear pack</Button>}
              />
            }
          />
        )}

        <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) { setCreateName(""); setCreateTemplate("default"); } }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nuevo pack</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Nombre</Label>
                <Input
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Ej. Caja de regalo especial…"
                  onKeyDown={(e) => { if (e.key === "Enter" && createName.trim()) void createBundle(); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Plantilla de inicio</Label>
                <p className="text-xs text-muted-foreground">Cada plantilla precarga pasos, reglas y textos típicos. Podés modificarlo todo después.</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {BUNDLE_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => setCreateTemplate(tpl.id)}
                      className={[
                        "flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-all",
                        createTemplate === tpl.id
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border hover:border-muted-foreground/40",
                      ].join(" ")}
                    >
                      <span className="text-xl leading-none">{tpl.emoji}</span>
                      <span className="text-xs font-medium leading-tight">{tpl.name}</span>
                      <span className="text-[10px] text-muted-foreground leading-tight line-clamp-2">{tpl.example}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button onClick={() => void createBundle()} disabled={saving || !createName.trim()}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear pack"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ClinicalPageShell>
    </div>
  );
}

function BundleEditor(props: {
  record: BundleRecord;
  products: MarketProduct[];
  categories: CategoryHit[];
  saving: boolean;
  onBack: () => void;
  onChangeDraft: (d: FlowDefinition) => void;
  onSave: () => void;
  onPublish: () => void;
}) {
  const tid = useWorkspaceTenantId();
  const draft = props.record.draft;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewViewport, setPreviewViewport] = useState<"desktop" | "mobile">("mobile");
  const [productPickerStepId, setProductPickerStepId] = useState<string | null>(null);
  const steps = useMemo(() => [...draft.graph.steps].sort((a, b) => a.order - b.order), [draft.graph.steps]);
  const topSteps = steps.filter((s) => !s.parentId);
  const radioSteps = steps.filter((s) => s.type === "radio" || s.type === "select");

  const updateSteps = (nextSteps: FlowStep[]) => {
    const entryStepId = nextSteps.find((s) => !s.parentId)?.id ?? nextSteps[0]?.id ?? draft.graph.entryStepId;
    props.onChangeDraft({
      ...draft,
      graph: { ...draft.graph, steps: nextSteps.map((s, i) => ({ ...s, order: i })), entryStepId },
      updatedAt: new Date().toISOString(),
    });
  };

  const replaceDraft = (next: FlowDefinition) => {
    props.onChangeDraft({ ...next, updatedAt: new Date().toISOString() });
  };

  const addStep = (type: FlowStepType) => {
    const labels: Partial<Record<FlowStepType, string>> = {
      product_picker: "Elegí un producto",
      radio: "Elegí una opción",
      textarea: "Mensaje personalizado",
      summary: "Resumen",
      container: "Armá tu pack",
    };
    const step = newStep({
      type,
      label: labels[type] ?? type,
      key:
        type === "product_picker"
          ? `pick_${draft.graph.steps.length + 1}`
          : type === "radio"
            ? `option_${draft.graph.steps.length + 1}`
            : type === "summary"
              ? "summary"
              : type === "textarea"
                ? "message"
                : undefined,
      required: type === "product_picker" || type === "radio",
      selectionMode: type === "product_picker" ? "single" : type === "radio" ? "single" : "none",
      qty: type === "product_picker" ? { mode: "exact", exact: 1, min: 1, max: 1 } : { mode: "none" },
      field:
        type === "radio"
          ? {
              options: [
                { value: "standard", label: "Estándar" },
                { value: "premium", label: "Premium" },
              ],
            }
          : type === "textarea"
            ? { placeholder: "Escribí un mensaje…" }
            : undefined,
    });

    let bindings = draft.sourceBindings;
    if (type === "product_picker") {
      const bindId = `bind_${crypto.randomUUID().replace(/-/g, "").slice(0, 8)}`;
      step.sourceBindingIds = [bindId];
      bindings = [
        ...bindings,
        {
          id: bindId,
          sourceKey: "catalog.products",
          stepId: step.id,
          query: {},
          lazy: true,
          required: true,
          selectionMode: "single",
          minSelections: 1,
          maxSelections: 1,
        },
      ];
    }

    // Insert before summary if present
    const withoutSummary = draft.graph.steps.filter((s) => s.type !== "summary");
    const summaries = draft.graph.steps.filter((s) => s.type === "summary");
    const nextSteps =
      type === "summary"
        ? [...draft.graph.steps, { ...step, order: draft.graph.steps.length }]
        : [...withoutSummary, { ...step, order: withoutSummary.length }, ...summaries];

    replaceDraft({
      ...draft,
      sourceBindings: bindings,
      graph: {
        ...draft.graph,
        steps: nextSteps.map((s, i) => ({ ...s, order: i })),
        entryStepId: draft.graph.steps.length ? draft.graph.entryStepId : step.id,
      },
    });
  };

  const move = (id: string, dir: -1 | 1) => {
    const idx = topSteps.findIndex((s) => s.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= topSteps.length) return;
    const nextTop = [...topSteps];
    const tmp = nextTop[idx]!;
    nextTop[idx] = nextTop[j]!;
    nextTop[j] = tmp;
    const children = steps.filter((s) => s.parentId);
    updateSteps([...nextTop, ...children]);
  };

  const removeStep = (id: string) => {
    const drop = new Set(steps.filter((s) => s.id === id || s.parentId === id).map((s) => s.id));
    const nextSteps = steps.filter((s) => !drop.has(s.id)).map((s, i) => ({ ...s, order: i }));
    replaceDraft({
      ...draft,
      graph: {
        ...draft.graph,
        steps: nextSteps,
        entryStepId: nextSteps.find((s) => !s.parentId)?.id ?? nextSteps[0]?.id ?? draft.graph.entryStepId,
      },
      sourceBindings: draft.sourceBindings.filter((b) => !drop.has(b.stepId)),
    });
  };

  const patchStep = (id: string, patch: Partial<FlowStep>) => {
    updateSteps(steps.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const setSourceMode = (step: FlowStep, mode: SourceMode) => {
    const bind = bindingForStep(draft, step);
    if (!bind) return;
    const query =
      mode === "category"
        ? { categoriaId: props.categories[0]?.id ?? "" }
        : mode === "products"
          ? { productIds: [] as string[] }
          : {};
    replaceDraft({
      ...draft,
      sourceBindings: draft.sourceBindings.map((b) => (b.id === bind.id ? { ...b, query } : b)),
    });
  };

  const setSourceCategory = (step: FlowStep, categoriaId: string) => {
    const bind = bindingForStep(draft, step);
    if (!bind) return;
    replaceDraft({
      ...draft,
      sourceBindings: draft.sourceBindings.map((b) =>
        b.id === bind.id ? { ...b, query: { categoriaId } } : b,
      ),
    });
  };


  const setSourceProducts = (step: FlowStep, productIds: string[]) => {
    const bind = bindingForStep(draft, step);
    if (!bind) return;
    replaceDraft({
      ...draft,
      sourceBindings: draft.sourceBindings.map((b) =>
        b.id === bind.id ? { ...b, query: { productIds } } : b,
      ),
    });
  };

  const syncQtyToBinding = (step: FlowStep, min?: number, max?: number) => {
    const bind = bindingForStep(draft, step);
    const qty = {
      mode: "min_max" as const,
      min: min ?? step.qty.min,
      max: max ?? step.qty.max,
    };
    const selectionMode = (max ?? 1) <= 1 ? ("single" as const) : ("multiple" as const);
    const nextSteps = steps.map((s) =>
      s.id === step.id ? { ...s, qty, selectionMode } : s,
    );
    replaceDraft({
      ...draft,
      graph: { ...draft.graph, steps: nextSteps.map((s, i) => ({ ...s, order: i })) },
      sourceBindings: bind
        ? draft.sourceBindings.map((b) =>
            b.id === bind.id
              ? {
                  ...b,
                  selectionMode,
                  minSelections: qty.min,
                  maxSelections: qty.max,
                }
              : b,
          )
        : draft.sourceBindings,
    });
  };

  const patchRule = (id: string, patch: Partial<FlowRule>) => {
    replaceDraft({
      ...draft,
      rules: draft.rules.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    });
  };

  const addDiscountRule = () => {
    const trigger = radioSteps[0];
    const opt = trigger?.field?.options?.[0];
    const id = `rule_${crypto.randomUUID().replace(/-/g, "").slice(0, 8)}`;
    replaceDraft({
      ...draft,
      rules: [
        ...draft.rules,
        {
          id,
          key: id,
          name: trigger && opt ? `Si ${trigger.label} = ${opt.label} → 10% dto` : "Descuento 10% sobre el total",
          enabled: true,
          priority: 5,
          when: trigger
            ? { type: "variable", key: trigger.key, op: "eq", value: opt?.value ?? "premium" }
            : { type: "always" },
          effects: [{ type: "discount_percent", percent: 10, priority: 5 }],
        },
      ],
    });
  };

  const describeSource = (step: FlowStep): string => {
    const bind = bindingForStep(draft, step);
    const mode = sourceModeOf(bind);
    if (mode === "category") {
      const catId = String(bind?.query.categoriaId ?? "");
      const name = props.categories.find((c) => c.id === catId)?.name ?? catId;
      return name ? `Categoría: ${name}` : "Categoría (sin elegir)";
    }
    if (mode === "products") {
      const n = Array.isArray(bind?.query.productIds) ? (bind!.query.productIds as string[]).length : 0;
      if (!n) return "Ningún producto elegido";
      return `${n} producto${n === 1 ? "" : "s"} fijo${n === 1 ? "" : "s"}`;
    }
    return "Todo el catálogo activo";
  };

  return (
    <ClinicalPageShell>
      <MerchantExperienceBar screenId="bundles" />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <button type="button" className="text-sm text-muted-foreground mb-1" onClick={props.onBack}>
            ← Packs
          </button>
          <h1 className="text-2xl font-semibold tracking-tight">{draft.name}</h1>
          <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-2">
            <LifecycleBadge status={props.record.lifecycleStatus} />
            {props.record.dirty && props.record.lifecycleStatus === "published" ? (
              <span className="text-amber-600 dark:text-amber-400 text-xs">{lifecycleStatusLabel("dirty")}</span>
            ) : null}
          </p>
          <details className="text-xs text-muted-foreground mt-1">
            <summary className="cursor-pointer select-none">Detalles técnicos</summary>
            <div className="mt-2 space-y-2">
              <p className="font-mono break-all">
                Identificador web: {draft.slug}
                {props.record.version > 0 ? ` · versión publicada ${props.record.version}` : ""}
              </p>
              <div className="space-y-1">
                <Label className="text-xs">Identificador en el enlace</Label>
                <Input value={draft.slug} onChange={(e) => replaceDraft({ ...draft, slug: e.target.value })} />
              </div>
            </div>
          </details>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)}>
            <Eye className="h-4 w-4 mr-1" /> Probar pack
          </Button>
          <Button variant="outline" onClick={() => setPreviewOpen(true)}>
            <Eye className="h-4 w-4 mr-1" /> Probar pack
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-lg border bg-muted/40 p-4 text-sm space-y-2">
        <p className="font-medium text-foreground">Cómo funciona este pack</p>
        <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
          <li>
            Cada <strong className="text-foreground font-medium">paso</strong> es una elección del cliente.
          </li>
          <li>
            En un paso de productos definís <strong className="text-foreground font-medium">de dónde salen</strong>: una categoría
            del catálogo, o una lista fija de productos.
          </li>
          <li>
            El <strong className="text-foreground font-medium">total</strong> = suma de precios del catálogo de lo elegido en cada
            paso.
          </li>
          <li>
            Las <strong className="text-foreground font-medium">reglas</strong> pueden aplicar un dto/recargo según una opción previa
            (ej. si eligió Premium → 10% off).
          </li>
        </ol>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 border rounded-lg p-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={draft.name} onChange={(e) => replaceDraft({ ...draft, name: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <div className="flex items-center justify-between">
                <Label>Descripción</Label>
                <RenataLauncher
                  entityType="bundle"
                  entityId={draft.id}
                  entityName={draft.name}
                  field="description"
                  intent="write_description"
                  currentValue={draft.description ?? ""}
                  mode="menu"
                  label="Mejorar con Renata"
                />
              </div>
              <Textarea
                value={draft.description ?? ""}
                onChange={(e) => replaceDraft({ ...draft, description: e.target.value })}
                onFocus={() =>
                  setRenataWorkspaceFocus({
                    entity: "bundle",
                    entityId: draft.id,
                    entityName: draft.name,
                    draft: { field: "description", currentValue: draft.description },
                  })
                }
                placeholder="Qué incluye el pack, para quién es, cómo se arma…"
                rows={3}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Imagen del pack</Label>
              <ImageUploadField
                kind="product"
                value={draft.imageUrl ?? null}
                onChange={(url) => replaceDraft({ ...draft, imageUrl: url ?? undefined })}
                tenantId={tid}
              />
              <p className="text-xs text-muted-foreground">
                Se muestra en Home, listado de packs y la página del pack (antes de armar).
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground mr-1">Agregar paso:</span>
            <Button size="sm" variant="secondary" onClick={() => addStep("product_picker")}>
              + Productos
            </Button>
            <Button size="sm" variant="secondary" onClick={() => addStep("radio")}>
              + Opción (dto / tamaño)
            </Button>
            <Button size="sm" variant="secondary" onClick={() => addStep("textarea")}>
              + Mensaje
            </Button>
            <Button size="sm" variant="secondary" onClick={() => addStep("summary")}>
              + Resumen
            </Button>
          </div>

          <div className="space-y-3">
            {topSteps.length === 0 ? (
              <p className="text-sm text-muted-foreground border rounded-lg p-6 text-center">
                Todavía no hay pasos. Empezá con “+ Productos” para la primera elección.
              </p>
            ) : null}
            {topSteps.map((s, idx) => {
              const bind = bindingForStep(draft, s);
              const mode = sourceModeOf(bind);
              const stepNum = idx + 1;
              return (
                <div key={s.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1 flex-1 min-w-0">
                      <div className="text-xs font-medium text-muted-foreground">
                        Paso {stepNum} · {STEP_TYPE_LABEL[s.type] ?? s.type}
                      </div>
                      <Input
                        value={s.label}
                        onChange={(e) => patchStep(s.id, { label: e.target.value })}
                        placeholder="Ej. Elegí la bebida"
                      />
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => move(s.id, -1)} aria-label="Subir">
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => move(s.id, 1)} aria-label="Bajar">
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => removeStep(s.id)} aria-label="Eliminar">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <Textarea
                    placeholder="Ayuda corta para el cliente (opcional)"
                    value={s.description ?? ""}
                    onChange={(e) => patchStep(s.id, { description: e.target.value })}
                    rows={2}
                  />

                  {s.type === "product_picker" ? (
                    <div className="space-y-3 rounded-md bg-muted/50 p-3">
                      <div>
                        <Label className="text-xs">¿De dónde salen los productos de este paso?</Label>
                        <div className="mt-2 flex flex-col gap-2">
                          {(
                            [
                              ["all", "Todo el catálogo"],
                              ["category", "Una categoría"],
                              ["products", "Productos específicos"],
                            ] as const
                          ).map(([value, label]) => (
                            <label key={value} className="flex items-center gap-2 text-sm">
                              <input
                                type="radio"
                                name={`src-${s.id}`}
                                checked={mode === value}
                                onChange={() => setSourceMode(s, value)}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>

                      {mode === "category" ? (
                        <div className="space-y-1">
                          <Label className="text-xs">Categoría</Label>
                          {props.categories.length ? (
                            <Select
                              value={String(bind?.query.categoriaId ?? "")}
                              onValueChange={(v) => setSourceCategory(s, v)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Elegí categoría" />
                              </SelectTrigger>
                              <SelectContent>
                                {props.categories.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>
                                    {c.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="text-xs text-muted-foreground">No hay categorías en el catálogo.</p>
                          )}
                        </div>
                      ) : null}

                      {mode === "products" ? (
                        <div className="space-y-2">
                          <Label className="text-xs">Productos disponibles en este paso</Label>
                          {(() => {
                            const ids = Array.isArray(bind?.query.productIds)
                              ? (bind!.query.productIds as string[])
                              : [];
                            const names = ids
                              .map((id) => props.products.find((p) => p.id === id)?.name)
                              .filter(Boolean)
                              .slice(0, 3);
                            return (
                              <div className="flex flex-wrap items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setProductPickerStepId(s.id)}
                                >
                                  <Search className="mr-1.5 h-3.5 w-3.5" />
                                  {ids.length ? "Cambiar productos" : "Elegir productos"}
                                </Button>
                                <span className="text-xs text-muted-foreground">
                                  {ids.length
                                    ? `${ids.length} elegido${ids.length === 1 ? "" : "s"}${
                                        names.length
                                          ? ` · ${names.join(", ")}${ids.length > 3 ? "…" : ""}`
                                          : ""
                                      }`
                                    : "Ninguno elegido todavía"}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      ) : null}

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Mín. a elegir</Label>
                          <Input
                            type="number"
                            min={0}
                            value={s.qty.min ?? s.qty.exact ?? 1}
                            onChange={(e) => syncQtyToBinding(s, Number(e.target.value), s.qty.max ?? s.qty.exact ?? 1)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Máx. a elegir</Label>
                          <Input
                            type="number"
                            min={1}
                            value={s.qty.max ?? s.qty.exact ?? 1}
                            onChange={(e) => syncQtyToBinding(s, s.qty.min ?? 0, Number(e.target.value))}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Fuente actual: {describeSource(s)}</p>
                    </div>
                  ) : null}

                  {s.type === "radio" ? (
                    <div className="space-y-2 rounded-md bg-muted/50 p-3">
                      <p className="text-xs text-muted-foreground">
                        Opciones de elección (tamaño, estilo, nivel). Si el cliente debe elegir un producto concreto,
                        usá un paso de Productos.
                      </p>
                      <Label className="text-xs">Opciones (una por línea: valor|etiqueta)</Label>
                      <Textarea
                        rows={4}
                        value={(s.field?.options ?? []).map((o) => `${o.value}|${o.label}`).join("\n")}
                        onChange={(e) => {
                          const options = e.target.value
                            .split("\n")
                            .map((line) => line.trim())
                            .filter(Boolean)
                            .map((line) => {
                              const [value, ...rest] = line.split("|");
                              const label = rest.join("|").trim() || value!.trim();
                              return { value: value!.trim(), label };
                            });
                          patchStep(s.id, { field: { ...s.field, options } });
                        }}
                      />
                      <div className="space-y-1">
                        <Label className="text-xs">Clave interna (para reglas)</Label>
                        <Input
                          value={s.key}
                          onChange={(e) => patchStep(s.id, { key: e.target.value.replace(/\s+/g, "_") })}
                        />
                      </div>
                    </div>
                  ) : null}

                  {s.type === "summary" ? (
                    <p className="text-xs text-muted-foreground">
                      Acá el cliente ve la suma de lo elegido + descuentos de reglas, y agrega al carrito.
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="border rounded-lg p-4 space-y-3">
            <div className="font-medium">Precio del pack</div>
            <p className="text-xs text-muted-foreground">
              Por defecto se suma el catálogo. Podés fijar un precio de pack y/o desactivar descuentos de reglas.
            </p>
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm">Precio fijo</Label>
              <Switch
                checked={draft.outputSpec.fixedPrice != null}
                onCheckedChange={(on) =>
                  replaceDraft({
                    ...draft,
                    outputSpec: {
                      ...draft.outputSpec,
                      fixedPrice: on ? Number(draft.outputSpec.fixedPrice ?? 0) : null,
                    },
                  })
                }
              />
            </div>
            {draft.outputSpec.fixedPrice != null ? (
              <div className="space-y-1">
                <Label className="text-xs">Monto fijo del pack</Label>
                <Input
                  type="number"
                  min={0}
                  step={100}
                  value={Number(draft.outputSpec.fixedPrice ?? 0)}
                  onChange={(e) =>
                    replaceDraft({
                      ...draft,
                      outputSpec: {
                        ...draft.outputSpec,
                        fixedPrice: Math.max(0, Number(e.target.value) || 0),
                      },
                    })
                  }
                />
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-sm">Permitir descuentos de reglas</Label>
                <p className="text-[11px] text-muted-foreground">Si está off, solo suma o precio fijo.</p>
              </div>
              <Switch
                checked={draft.outputSpec.allowDiscounts !== false}
                onCheckedChange={(on) =>
                  replaceDraft({
                    ...draft,
                    outputSpec: { ...draft.outputSpec, allowDiscounts: on },
                  })
                }
              />
            </div>
          </div>
          <div className="border rounded-lg p-4 space-y-3">
            <div className="font-medium">Reglas de precio</div>
            <p className="text-xs text-muted-foreground">
              Primero se suman los precios del catálogo de todos los pasos. Después, si una regla coincide, se aplica
              dto o recargo sobre ese total.
            </p>
            {draft.rules.length === 0 ? (
              <p className="text-xs text-muted-foreground">Sin reglas — el total es solo la suma.</p>
            ) : null}
            {draft.rules.map((r) => {
              const when = r.when as { type?: string; key?: string; value?: unknown; op?: string };
              const effect = r.effects.find((e) => e.type === "discount_percent" || e.type === "discount_fixed");
              const pct = effect && effect.type === "discount_percent" ? effect.percent : undefined;
              return (
                <div key={r.id} className="text-sm border rounded p-3 space-y-2 bg-muted/30">
                  <Input value={r.name} onChange={(e) => patchRule(r.id, { name: e.target.value })} />
                  {when.type === "variable" ? (
                    <div className="grid gap-2">
                      <div>
                        <Label className="text-xs">Si la opción (clave)</Label>
                        <Select
                          value={String(when.key ?? "")}
                          onValueChange={(key) =>
                            patchRule(r.id, {
                              when: { type: "variable", key, op: "eq", value: when.value },
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Paso opción" />
                          </SelectTrigger>
                          <SelectContent>
                            {radioSteps.map((rs) => (
                              <SelectItem key={rs.id} value={rs.key}>
                                {rs.label} ({rs.key})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Vale</Label>
                        <Input
                          value={String(when.value ?? "")}
                          onChange={(e) =>
                            patchRule(r.id, {
                              when: { type: "variable", key: String(when.key ?? ""), op: "eq", value: e.target.value },
                            })
                          }
                          placeholder="premium"
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Condición: {when.type ?? "custom"}</p>
                  )}
                  <div>
                    <Label className="text-xs">Descuento %</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={pct ?? 0}
                      onChange={(e) => {
                        const percent = Number(e.target.value) || 0;
                        const other = r.effects.filter((x) => x.type !== "discount_percent");
                        patchRule(r.id, {
                          effects: [...other, { type: "discount_percent", percent, priority: 5 }],
                        });
                      }}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={r.enabled}
                      onChange={(e) => patchRule(r.id, { enabled: e.target.checked })}
                    />
                    Activa
                  </label>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    onClick={() =>
                      replaceDraft({ ...draft, rules: draft.rules.filter((x) => x.id !== r.id) })
                    }
                  >
                    Quitar regla
                  </Button>
                </div>
              );
            })}
            <Button size="sm" variant="secondary" onClick={addDiscountRule}>
              + Regla de descuento
            </Button>
            {!radioSteps.length ? (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Tip: agregá un paso “Opción” (ej. tipo de caja) para condicionar el dto. Sin eso, la regla puede
                aplicarse siempre.
              </p>
            ) : null}
          </div>

          <CrudHelpCard title="¿Cómo funciona un pack?">
            <ul>
              <li><strong>Pasos</strong>: cada paso le pide una elección al cliente. Podés usar Elegir productos, Opción (radio), Mensaje de texto o Resumen.</li>
              <li><strong>Fuentes</strong>: en cada paso de productos, definís de qué categoría o productos concretos puede elegir el cliente.</li>
              <li><strong>Reglas</strong>: aplican descuentos o recargos según la elección de un paso de tipo Opción.</li>
              <li><strong>Publicar</strong>: activa el pack en la tienda.</li>
            </ul>
          </CrudHelpCard>

          <div className="border rounded-lg p-4 text-sm space-y-3">
            <div className="font-medium">¿Dónde se ve en la tienda?</div>
            <div className="space-y-2 text-muted-foreground text-xs">
              <p>
                <strong className="text-foreground font-medium">1. Publicar</strong> activa el pack en la tienda.
              </p>
              <p>
                Enlace: <code className="text-[11px] break-all">/market/&lt;tienda&gt;/bundle/{draft.slug}</code>
              </p>
              <p>
                <strong className="text-foreground font-medium">2. Home</strong>: en Experiencia → Home agregá el bloque Pack con el nombre corto <code className="text-[11px]">{draft.slug}</code>.
              </p>
              <Button asChild size="sm" variant="outline" className="w-full">
                <Link to="/app/experience">Ir a Experiencia (Home)</Link>
              </Button>
            </div>
          </div>
        </aside>
      </div>

      <ProductSourcePickerDialog
        open={productPickerStepId != null}
        onOpenChange={(open) => {
          if (!open) setProductPickerStepId(null);
        }}
        stepLabel={
          productPickerStepId
            ? steps.find((st) => st.id === productPickerStepId)?.label ?? "Productos"
            : "Productos"
        }
        products={props.products}
        categories={props.categories}
        selectedIds={
          productPickerStepId
            ? (() => {
                const step = steps.find((st) => st.id === productPickerStepId);
                const b = step ? bindingForStep(draft, step) : undefined;
                return Array.isArray(b?.query.productIds) ? ([...b!.query.productIds] as string[]) : [];
              })()
            : []
        }
        onConfirm={(ids) => {
          const step = steps.find((st) => st.id === productPickerStepId);
          if (step) setSourceProducts(step, ids);
          setProductPickerStepId(null);
        }}
      />

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col gap-0 p-0">
          <DialogHeader className="px-4 py-3 border-b flex flex-row items-center justify-between">
            <DialogTitle className="text-sm">Vista previa — {draft.name}</DialogTitle>
            <div className="flex items-center gap-1 rounded-md border p-0.5">
              <button
                onClick={() => setPreviewViewport("mobile")}
                className={["p-1.5 rounded transition-colors", previewViewport === "mobile" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"].join(" ")}
                title="Vista mobile"
              >
                <Smartphone className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setPreviewViewport("desktop")}
                className={["p-1.5 rounded transition-colors", previewViewport === "desktop" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"].join(" ")}
                title="Vista desktop"
              >
                <Monitor className="h-3.5 w-3.5" />
              </button>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto bg-muted/30 p-4 flex justify-center">
            <div
              className="bg-background rounded-xl shadow-lg overflow-y-auto transition-all"
              style={previewViewport === "mobile" ? { width: 390, maxHeight: "70vh" } : { width: "100%", maxHeight: "70vh" }}
            >
              <BundleRuntime definition={draft} products={props.products} channel="preview" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <div className="sticky bottom-0 z-20 mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-border bg-background py-3">
        <Button type="button" variant="outline" onClick={props.onBack} disabled={props.saving}>
          Cancelar
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={props.onSave} disabled={props.saving}>
            Guardar
          </Button>
          <Button type="button" onClick={props.onPublish} disabled={props.saving}>
            <Upload className="h-4 w-4 mr-1" /> Publicar
          </Button>
        </div>
      </div>
    </ClinicalPageShell>
  );
}

function ProductSourcePickerDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stepLabel: string;
  products: MarketProduct[];
  categories: CategoryHit[];
  selectedIds: string[];
  onConfirm: (ids: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState<string>("all");
  const [draftIds, setDraftIds] = useState<string[]>(props.selectedIds);

  useEffect(() => {
    if (props.open) {
      setDraftIds(props.selectedIds);
      setQuery("");
      setCategoryId("all");
    }
  }, [props.open, props.selectedIds]);

  const catNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of props.categories) m.set(c.id, c.name);
    return m;
  }, [props.categories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return props.products.filter((p) => {
      if (categoryId !== "all" && (p.categoriaId ?? "") !== categoryId) return false;
      if (!q) return true;
      const hay = `${p.name} ${p.slug ?? ""} ${catNameById.get(p.categoriaId ?? "") ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [props.products, query, categoryId, catNameById]);

  const draftSet = useMemo(() => new Set(draftIds), [draftIds]);

  const toggle = (id: string) => {
    setDraftIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectVisible = () => {
    setDraftIds((prev) => {
      const next = new Set(prev);
      for (const p of filtered) next.add(p.id);
      return [...next];
    });
  };

  const clearVisible = () => {
    const visible = new Set(filtered.map((p) => p.id));
    setDraftIds((prev) => prev.filter((id) => !visible.has(id)));
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="!flex max-h-[85vh] max-w-xl !flex-col gap-0 overflow-hidden !p-0 sm:max-w-2xl">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>Productos — {props.stepLabel}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Marcá qué productos puede elegir el cliente en este paso.
          </p>
        </DialogHeader>

        <div className="shrink-0 space-y-3 border-b px-6 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre…"
              className="pl-9"
              autoFocus
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {props.categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="button" variant="ghost" size="sm" onClick={selectVisible} disabled={!filtered.length}>
              Marcar visibles
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={clearVisible} disabled={!filtered.length}>
              Limpiar visibles
            </Button>
            <span className="ml-auto text-xs text-muted-foreground">
              {draftIds.length} elegido{draftIds.length === 1 ? "" : "s"} · {filtered.length} visibles
            </span>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {filtered.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              No hay productos con ese filtro.
            </p>
          ) : (
            <ul className="space-y-0.5">
              {filtered.map((p) => {
                const on = draftSet.has(p.id);
                const cat = p.categoriaId ? catNameById.get(p.categoriaId) : null;
                return (
                  <li key={p.id}>
                    <label className="flex cursor-pointer items-center gap-3 rounded-md px-4 py-2.5 hover:bg-muted/60">
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0"
                        checked={on}
                        onChange={() => toggle(p.id)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{p.name}</span>
                        {cat ? (
                          <span className="block truncate text-xs text-muted-foreground">{cat}</span>
                        ) : null}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {Number(p.precioUnitario ?? 0).toFixed(0)}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setDraftIds([])}
            disabled={!draftIds.length}
          >
            Quitar todos
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => props.onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => props.onConfirm(draftIds)}>
              Confirmar ({draftIds.length})
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
